import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useSolanaTokens } from '../../hooks/useSolanaTokens';

const SOL_BLACK = '#000000';
const SOL_LIGHT = '#FFFFFF';
const SOL_PURPLE = '#9945FF';

const FooterContainer = styled.div`
  width: 100%;
  background: ${SOL_BLACK};
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 12px 24px;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.5);
  
  @media (max-width: 768px) {
    padding: 12px 16px;
    flex-direction: column;
    gap: 12px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const PriceDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  color: ${SOL_LIGHT};
  font-size: 14px;
`;

const PriceLabel = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
`;

const TokenLogo = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const SocialLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SocialLink = styled.a`
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: ${SOL_PURPLE};
    transform: translateY(-1px);
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  width: 300px;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 8px 16px 8px 36px;
  color: ${SOL_LIGHT};
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  outline: none;
  transition: all 0.2s;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    background: rgba(255, 255, 255, 0.15);
    border-color: ${SOL_PURPLE};
    box-shadow: 0 0 0 2px rgba(153, 69, 255, 0.1);
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.5);
  pointer-events: none;
  display: flex;
  align-items: center;
`;

const ExpandedSearchPanel = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  bottom: calc(100% + 12px);
  right: 0;
  width: 400px;
  background: ${SOL_BLACK};
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 16px;
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  max-height: 400px;
  overflow-y: auto;
  z-index: 90;
  
  @media (max-width: 768px) {
    width: 100%;
    right: 0;
    left: 0;
    bottom: calc(100% + 12px);
  }
`;

const SearchResultItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ResultTokenInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ResultSymbol = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${SOL_LIGHT};
`;

const ResultName = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
`;

const SentryPriceFooter: React.FC = () => {
  const [solPrice, setSolPrice] = useState<number | null>(null);
  const [footerSearch, setFooterSearch] = useState('');
  const { tokens: allSolanaTokens } = useSolanaTokens('');

  const filteredTokens = useMemo(() => {
    if (!footerSearch || footerSearch.length < 2) return [];
    const search = footerSearch.toLowerCase();
    return allSolanaTokens.filter(token => {
      const mintAddress = (token as any).mint_address || token.contract_address || '';
      return token.name.toLowerCase().includes(search) ||
        token.symbol.toLowerCase().includes(search) ||
        mintAddress.toLowerCase().includes(search);
    }).slice(0, 5);
  }, [allSolanaTokens, footerSearch]);

  useEffect(() => {
    const fetchSolPrice = async () => {
      try {
        const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd');
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        if (data.solana?.usd) {
          setSolPrice(data.solana.usd);
        }
      } catch (error) {
        console.error('[SentryPriceFooter] Failed to fetch SOL price:', error);
      }
    };

    fetchSolPrice();
    const interval = setInterval(fetchSolPrice, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatPrice = (price: number | null): string => {
    if (price === null) return '...';
    return `$${price.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const handleTokenClick = (token: any) => {
    const mintAddress = (token as any).mint_address || token.contract_address;
    if (mintAddress) {
      window.open(`https://dexscreener.com/solana/${mintAddress}`, '_blank');
    }
  };

  return (
    <FooterContainer>
      <LeftSection>
        <PriceDisplay>
          <TokenLogo src="/landing-assets/Solana.png" alt="SOL" />
          <PriceLabel>SOL Price:</PriceLabel>
          <span>{formatPrice(solPrice)}</span>
        </PriceDisplay>
        <SocialLinks>
          <SocialLink href="https://x.com/sentrylauncher" target="_blank" rel="noopener noreferrer" title="Follow Sentry on X">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </SocialLink>
          <SocialLink href="https://t.me/sentrylauncher" target="_blank" rel="noopener noreferrer" title="Join Sentry on Telegram">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.999 3.88a1.5 1.5 0 0 0-1.64-.27L2.42 11.46a1.5 1.5 0 0 0 .16 2.84l4.9 1.64 1.88 4.99a1.5 1.5 0 0 0 2.49.58l2.71-2.63 5.02 3.69a1.5 1.5 0 0 0 2.36-.89l3.08-15.39a1.5 1.5 0 0 0-.02-.66Zm-3.86 3.55-7.69 7.05a.75.75 0 0 0-.22.45l-.33 3.35-1.27-3.37a.75.75 0 0 0-.45-.45l-3.36-1.12 13.39-5.83-4.07 1.92Z"/>
            </svg>
          </SocialLink>
        </SocialLinks>
      </LeftSection>

      <RightSection>
        <ExpandedSearchPanel $isOpen={footerSearch.length >= 2 && filteredTokens.length > 0}>
          {filteredTokens.map((token) => {
            const mintAddress = (token as any).mint_address || token.contract_address;
            return (
              <SearchResultItem key={mintAddress} onClick={() => handleTokenClick(token)}>
                <TokenLogo src={token.logo_url || '/default-token.png'} alt={token.symbol} />
                <ResultTokenInfo>
                  <ResultSymbol>{token.symbol}</ResultSymbol>
                  <ResultName>{token.name}</ResultName>
                </ResultTokenInfo>
              </SearchResultItem>
            );
          })}
        </ExpandedSearchPanel>
        <SearchContainer>
          <SearchIconWrapper>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </SearchIconWrapper>
          <SearchInput
            type="text"
            placeholder="Search tokens..."
            value={footerSearch}
            onChange={(e) => setFooterSearch(e.target.value)}
          />
        </SearchContainer>
      </RightSection>
    </FooterContainer>
  );
};

export default SentryPriceFooter;
